import { useRef } from 'react';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useParams } from 'react-router';
import z from 'zod';

import { api } from '@api/client';
import {
  CustomDomainListResponse,
  RelayAuthentication,
  RelayHostnameListResponse,
  RelayHostnameObject,
  RelayObject,
  RelayRouteObject,
} from '@api/types';
import { useRecentActions } from '@components/features/Overview/QuickLinks/hook';
import { useStrongParams } from '@utils/params';

export const relayParams = z.object({
  teamUuid: z.string().nonempty(),
  appUuid: z.string().nonempty(),
  relayUuid: z.string().nonempty(),
});

function relaysCacheKey(appUuid: string) {
  return [appUuid, 'relays'];
}

export function useRelays(options = {}) {
  const { appUuid } = useParams();

  return useQuery({
    queryKey: relaysCacheKey(appUuid as string),
    queryFn: async () => {
      const client = api(appUuid as string);
      const response = await client.get('relays');
      return await response.json<{ data: RelayObject[] }>();
    },
    ...options,
  });
}

export function relaysQuery<T = { data: RelayObject[] }>(
  appUuid?: string,
  options?: Partial<UseQueryOptions<{ data: RelayObject[] }, unknown, T>>
): UseQueryOptions<{ data: RelayObject[] }, unknown, T> {
  return {
    queryKey: relaysCacheKey(appUuid as string),
    queryFn: async () => {
      const client = api(appUuid as string);
      const response = await client.get('relays');
      return await response.json<{ data: RelayObject[] }>();
    },
    enabled: appUuid != null,
    ...(options ?? {}),
  };
}

export function useRelaysForApp<T = { data: RelayObject[] }>(
  appUuid?: string,
  options?: Partial<UseQueryOptions<{ data: RelayObject[] }, unknown, T>>
) {
  return useQuery(relaysQuery(appUuid, options));
}

function relayCacheKey(appUuid: string, id: string) {
  return [appUuid, 'relays', id];
}

export const relayQuery = (appUuid: string, id: string) => ({
  queryKey: relayCacheKey(appUuid, id),
  queryFn: async () => {
    const client = api(appUuid);
    const response = await client.get(`relays/${id}`);
    return await response.json<RelayObject>();
  },
});
export function useRelay(id: string, opts = {}) {
  const { appUuid } = useParams();

  return useQuery({
    ...relayQuery(appUuid as string, id),
    ...opts,
  });
}

type CreateRelayArguments = {
  destinationDomain: string;
};

export function useCreateRelay() {
  const { appUuid } = useParams();
  const transaction = useCacheTransaction();

  return useMutation({
    mutationFn: async (relay: CreateRelayArguments) => {
      const client = api(appUuid as string);
      const response = await client.post('relays', { json: relay });
      return response.json<RelayObject>();
    },
    onError: () => {
      transaction.rollback();
    },
    onSuccess: () => {
      transaction.finalize();
    },
  });
}

type CachedQuery = { key: string[]; data: any };

function useCacheTransaction() {
  const client = useQueryClient();
  const store = useRef<CachedQuery[]>([]);

  const update = async (key: string[], cb: (data: any) => any) => {
    await client.cancelQueries(key);
    const previous = client.getQueryData(key);
    store.current.push({ key, data: previous });
    client.setQueryData(key, cb);
  };

  const rollback = () => {
    store.current.forEach(({ key, data }) => {
      client.setQueryData(key, data);
    });

    store.current = [];
  };

  const finalize = () => {
    store.current.forEach(({ key }) => {
      void client.invalidateQueries({ queryKey: key });
    });

    store.current = [];
  };

  return { update, rollback, finalize };
}

type UpdateRelayArguments = {
  destinationDomain?: string;
  encryptEmptyStrings?: boolean;
  routes?: RelayRouteObject[];
  authentication?: RelayAuthentication;
};

export function useUpdateRelay(relay: RelayObject) {
  const { appUuid } = useParams();
  const transaction = useCacheTransaction();

  return useMutation({
    mutationFn: async (args: Partial<UpdateRelayArguments>) => {
      const client = api(appUuid as string);
      const response = await client.patch(`relays/${relay.id}`, { json: args });
      return response.json<RelayObject>();
    },
    onMutate: async (newRelay: Partial<UpdateRelayArguments>) => {
      void transaction.update(
        relayCacheKey(appUuid as string, relay.id),
        (old: RelayObject) => ({ ...old, ...newRelay })
      );
    },
    onError: () => {
      transaction.rollback();
    },
    onSuccess: () => {
      transaction.finalize();
    },
  });
}

export function useDeleteRelay(relay: RelayObject) {
  const { appUuid } = useParams();
  const recents = useRecentActions();

  return useMutation({
    mutationFn: async () => {
      const client = api(appUuid as string);
      return await client.delete(`relays/${relay.id}`);
    },
    onSuccess: () => {
      recents.remove({ type: 'relay', id: relay.id });
    },
  });
}

export interface RelayHostnamesListQueryParams {
  appUuid: string;
  relayUuid: string;
}

export function relayHostnamesListQueryOptions(
  params: RelayHostnamesListQueryParams
) {
  return queryOptions({
    queryKey: ['relays/:relayUuid/hostnames', params.relayUuid],
    async queryFn() {
      const client = api(params.appUuid);
      const response = await client.get<RelayHostnameListResponse>(
        `relays/${params.relayUuid}/hostnames`
      );
      return response.json();
    },
  });
}

export interface CreateRelayHostnameParams {
  domain: string;
  validationMethod: 'HTTP' | 'TXT';
}

export function useCreateRelayHostname(relayUuid: string) {
  const { appUuid } = useStrongParams(relayParams);
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(params: CreateRelayHostnameParams) {
      const client = api(appUuid as string);
      const response = await client.post<RelayHostnameObject>(
        `relays/${relayUuid}/hostnames`,
        {
          json: {
            domain: params.domain,
            validationMethod: params.validationMethod,
          },
        }
      );
      return response.json();
    },
    onSuccess() {
      void queryClient.invalidateQueries(
        customDomainsListQueryOptions({ appUuid, relayUuid })
      );
      void queryClient.invalidateQueries(
        relayHostnamesListQueryOptions({ appUuid, relayUuid })
      );
    },
  });
}

export function useDeleteCustomDomain(relayUuid: string, hostname: string) {
  const { appUuid } = useStrongParams(relayParams);
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn() {
      const client = api(appUuid);
      return await client.delete(`relays/${relayUuid}/hostnames/${hostname}`, {
        json: {
          bypassDeleteProtection: true,
        },
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries(
        customDomainsListQueryOptions({ appUuid, relayUuid })
      );
      void queryClient.invalidateQueries(
        relayHostnamesListQueryOptions({ appUuid, relayUuid })
      );
    },
  });
}

export interface CustomDomainsListQueryParams {
  appUuid: string;
  relayUuid: string;
}

/**
 * @deprecated Use relayHostnamesListQueryOptions instead
 */
export function customDomainsListQueryOptions(
  params: CustomDomainsListQueryParams
) {
  return {
    queryKey: ['relays/:relayUuid/custom-domains', params.relayUuid],
    async queryFn() {
      const client = api(params.appUuid);
      const response = await client.get<CustomDomainListResponse>(
        `relays/${params.relayUuid}/custom-domains`
      );
      return response.json();
    },
  };
}
